import * as React from "react";
import { DOCS_URL, IS_DEPLOYMENT_ENV_PROD } from "../../../../envVariables";
import { Alert, Button, Card } from "antd";
import { CardContent } from "../../../../components/antd/card/CardContent";
import { CardTitle } from "../../../../components/antd/card/CardTitle";
import { ExternalLink } from "../../../../newcomponents/table/components/ExternalLink";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { Link } from "react-router-dom";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

interface Props {}
export const Addons: React.FC<Props> = ({}) => {
  const provider = useCpFormValue(`provider`);

  const isByokEnabled = useCpFormValue(`addons.byok.isEnabled`);
  const isAwsEcrEnabled = useCpFormValue(`addons.awsECR.isEnabled`);
  const isAwsEfsEnabled = useCpFormValue(`addons.awsEFS.isEnabled`);
  const isAwsElbEnabled = useCpFormValue(`addons.awsELB.isEnabled`);
  const isAzureAcrEnabled = useCpFormValue(`addons.azureACR.isEnabled`);
  const isAzureWorkloadIdentityEnabled = useCpFormValue(`addons.azureWorkloadIdentity.isEnabled`);
  const isLogsEnabled = useCpFormValue(`addons.logs.isEnabled`);
  const isMetricsEnabled = useCpFormValue(`addons.metrics.isEnabled`);
  const isNvidiaEnabled = useCpFormValue(`addons.nvidia.isEnabled`);
  const isRegistryMirrorEnabled = useCpFormValue(`addons.registryMirror.isEnabled`);
  const isDashboardEnabled = useCpFormValue(`addons.dashboard.isEnabled`);
  const isHeadlampEnabled = useCpFormValue(`addons.headlamp.isEnabled`);
  const kubeState = useCpFormValue(`addons.metrics.kubeState`);
  const nodeExporter = useCpFormValue(`addons.metrics.nodeExporter`);
  const cadvisor = useCpFormValue(`addons.metrics.cadvisor`);
  const { id } = useCpFormContext();

  const hasDashboardAddon = isDashboardEnabled || isHeadlampEnabled;
  const dashboardMetricsMissing = !isMetricsEnabled || !kubeState || !nodeExporter || !cadvisor;
  const showDashboardMetricsWarning = hasDashboardAddon && dashboardMetricsMissing;

  function handleEnableDashboardMetrics() {
    formSetValue(id, `addons.metrics.isEnabled`, true);
    formSetValue(id, `addons.metrics.kubeState`, true);
    formSetValue(id, `addons.metrics.nodeExporter`, true);
    formSetValue(id, `addons.metrics.cadvisor`, true);
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>Add-Ons</CardTitle>
        <CardContent>
          <div>
            <CpErrorWarning name="addonsroot" />
            {showDashboardMetricsWarning ? (
              <Alert
                className="mb-4"
                style={{ width: "fit-content" }}
                type="warning"
                showIcon
                message={
                  <div className="flex items-center gap-2">
                    <span>
                      The Metrics addon with Kube State, Node Exporter, and cAdvisor enabled is required for the
                      {isDashboardEnabled && isHeadlampEnabled
                        ? " Dashboard and Headlamp"
                        : isDashboardEnabled
                          ? " Dashboard"
                          : " Headlamp"}{" "}
                      to work correctly.
                    </span>
                    <Button size="small" type="primary" onClick={handleEnableDashboardMetrics}>
                      Enable Metrics and Required Flags
                    </Button>
                  </div>
                }
              />
            ) : null}
            <div className={`flex flex-wrap gap-4`}>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.byok.isEnabled`}>CPLN Platform</CpLabel>
                  <CpSwitch name={`addons.byok.isEnabled`} />
                </div>
                <div className="text-sm my-4">Deploy on Control Plane Platform with a BYOK Location</div>
                {isByokEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-cpln-platform`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.dashboard.isEnabled`}>Dashboard (Deprecated)</CpLabel>
                  <CpSwitch name={`addons.dashboard.isEnabled`} />
                </div>
                <div className="text-sm my-4">
                  This addon is deprecated, we suggest using Headlamp. Dashboard addon enables the Kubernetes Dashboard
                  UI for this cluster
                </div>
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.headlamp.isEnabled`}>Headlamp</CpLabel>
                  <CpSwitch name={`addons.headlamp.isEnabled`} />
                </div>
                <div className="text-sm my-4">Headlamp addon enables the Headlamp Dashboard UI for this cluster</div>
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.awsECR.isEnabled`}>AWS ECR</CpLabel>
                  <CpSwitch name={`addons.awsECR.isEnabled`} />
                </div>
                <div className="text-sm my-4">Access to private Docker container images in AWS ECR</div>
                {isAwsEcrEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-aws-ecr`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              {provider === "aws" ? (
                <>
                  <div className={`border p-4 rounded`} style={{ width: 300 }}>
                    <div className="flex items-center gap-2">
                      <CpLabel name={`addons.awsEFS.isEnabled`}>AWS EFS</CpLabel>
                      <CpSwitch name={`addons.awsEFS.isEnabled`} />
                    </div>
                    <div className="text-sm my-4">Use AWS Elastic File System</div>
                    {isAwsEfsEnabled ? (
                      <Link className="color-link cursor-pointer" to={`addon-aws-efs`}>
                        Configure
                      </Link>
                    ) : null}
                  </div>
                  <div className={`border p-4 rounded`} style={{ width: 300 }}>
                    <div className="flex items-center gap-2">
                      <CpLabel name={`addons.awsELB.isEnabled`}>AWS ELB</CpLabel>
                      <CpSwitch name={`addons.awsELB.isEnabled`} />
                    </div>
                    <div className="text-sm my-4">Add support for Network Load Balancer (NLB)</div>
                    {isAwsElbEnabled ? (
                      <Link className="color-link cursor-pointer" to={`addon-aws-elb`}>
                        Configure
                      </Link>
                    ) : null}
                  </div>
                </>
              ) : null}
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.awsWorkloadIdentity.isEnabled`}>AWS Workload Identity</CpLabel>
                  <CpSwitch name={`addons.awsWorkloadIdentity.isEnabled`} />
                </div>
                <div className="text-sm mt-4">
                  <span className="text-sm my-4">Allow your pods to assume AWS IAM Roles</span>
                  <ExternalLink
                    className="ml-2"
                    label="Learn more"
                    url={`${DOCS_URL}/mk8s/add-ons/aws_workload_identity`}
                    disableCopy
                  />
                </div>
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.azureACR.isEnabled`}>Azure Container Registry</CpLabel>
                  <CpSwitch name={`addons.azureACR.isEnabled`} />
                </div>
                <div className="text-sm my-4">Access to private Docker container images in ACR</div>
                {isAzureAcrEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-azure-acr`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.azureWorkloadIdentity.isEnabled`}>Azure Workload Identity</CpLabel>
                  <CpSwitch name={`addons.azureWorkloadIdentity.isEnabled`} />
                </div>
                <div className="text-sm my-4">Allow your pods to assume Azure Identities</div>
                {isAzureWorkloadIdentityEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-azure-workload-identity`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.localPathStorage.isEnabled`}>Local Path Storage</CpLabel>
                  <CpSwitch name={`addons.localPathStorage.isEnabled`} />
                </div>
                <div className="text-sm my-4">Create PVCs backed by local volumes</div>
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.logs.isEnabled`}>Logs</CpLabel>
                  <CpSwitch name={`addons.logs.isEnabled`} />
                </div>
                <div className="text-sm my-4">Let Control Plane collect and store pod logs for this cluster</div>
                {isLogsEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-logs`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.metrics.isEnabled`}>Metrics</CpLabel>
                  <CpSwitch name={`addons.metrics.isEnabled`} />
                </div>
                <div className="text-sm my-4">
                  Let Control Plane collect and store Prometheus metrics for this cluster
                </div>
                {isMetricsEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-metrics`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              {IS_DEPLOYMENT_ENV_PROD ? null : (
                <div className={`border p-4 rounded`} style={{ width: 300 }}>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`addons.nvidia.isEnabled`}>NVIDIA</CpLabel>
                    <CpSwitch name={`addons.nvidia.isEnabled`} />
                  </div>
                  <div className="text-sm my-4">
                    Install all required components to run GPU workloads on this cluster
                  </div>
                  {isNvidiaEnabled ? (
                    <Link className="color-link cursor-pointer" to={`addon-nvidia`}>
                      Configure
                    </Link>
                  ) : null}
                </div>
              )}
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.registryMirror.isEnabled`}>Registry Mirror</CpLabel>
                  <CpSwitch name={`addons.registryMirror.isEnabled`} />
                </div>
                <div className="text-sm my-4">A pull through cache for all container images</div>
                {isRegistryMirrorEnabled ? (
                  <Link className="color-link cursor-pointer" to={`addon-registry-mirror`}>
                    Configure
                  </Link>
                ) : null}
              </div>
              <div className={`border p-4 rounded`} style={{ width: 300 }}>
                <div className="flex items-center gap-2">
                  <CpLabel name={`addons.sysbox.isEnabled`}>Sysbox</CpLabel>
                  <CpSwitch name={`addons.sysbox.isEnabled`} />
                </div>
                <div className="text-sm mt-4">
                  <span className="text-sm my-4">Use cri-o container runtime with better isolation</span>
                  <ExternalLink
                    className="ml-2"
                    label={`Learn more`}
                    url={`https://github.com/nestybox/sysbox`}
                    disableCopy
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
